import {
  DashboardConfig,
  PageConfig,
} from '../../../../common/components/dashboards/types';
import { TimeSliderTypes } from '../../../../common/components/timeslider/types';
import { Languages } from '../../../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  TimeSelectionType,
} from '../../../../types';
import {
  customField3SupervisorFilter,
  getAvgWorkhourByBranchChart,
  getAvgWorkhourFactbox,
  getHeadcountByEmploymentTypeChart,
  getIndividualsChart,
  getRevenuePerWorkhourChart,
  getWorkhourChart,
} from './jtb-hcm-chart-helpers';

const DASHBOARD_ID = 'jtb_human_capital_management';

const getLatestMonthTimeSelection = (
  latestMonth: string
): CalendarYearSingleValueByMonthsTimeSelection => ({
  type: TimeSelectionType.CalendarYearSingleValueByMonths,
  input: { start: latestMonth, end: latestMonth },
});

// ============================================================================
// Page 1: Productivity Driver Analysis (労働時間生産性)
// ============================================================================

const page1Header = `- **目的**：営業収入の成長が「労働時間の増加」に依存していないか、拠点別の生産性（時間当たり売上）と組織構成（雇用区分別の比率）を月次で検証し、持続可能な成長モデルを維持できているかを確認
- **今月のハイライト**：B拠点において、総労働時間の増加に対して営業収入が伸び悩む「労働時間依存」の傾向を検知`;

const page1Footer = `- 脚注1：ツーリズム（法人）西日本の各事業部・支店について初期的に分析
- 注釈2：労働時間あたり営業収入は「売上 ÷ 総労働時間」＝ 1時間あたり売上を算出`;

const page1Config: PageConfig = {
  pageId: '1',
  dashboardId: DASHBOARD_ID,
  pageNumber: 1,
  enabled: true,
  title: {
    [Languages.EN]: 'Productivity Driver Analysis',
    [Languages.JA]: '労働時間生産性',
  },
  header: page1Header,
  footer: page1Footer,
  pageOptions: {
    displayOptions: {
      enabled: false,
    },
    timeOptions: {
      timeSliderEnabled: true,
      timeSliderType: TimeSliderTypes.POINT,
    },
    filterTray: {
      // Segmentation and Filter should be hidden as per spec
      enabled: false,
    },
    dataView: {
      enabled: false,
    },
    segmentationLevel1: {
      enabled: false,
    },
    segmentationLevel2: {
      enabled: false,
    },
    benchmark: {
      enabled: false,
    },
    forecast: {
      enabled: false,
    },
  },
  gridCols: 20,
  configType: 'componentConfigs',
  components: ({ storyboardDateUtils }) => {
    const timeSelection = getLatestMonthTimeSelection(
      storyboardDateUtils.nowEndOfMonth
    );
    return [
      // Row 1: Left Top - Revenue per Workhour
      getRevenuePerWorkhourChart(timeSelection),
      // Row 1: Right Top - Workhour
      getWorkhourChart(timeSelection),
      // Row 2: Left Bottom - Individuals
      getIndividualsChart(timeSelection),
      // Row 2: Right Bottom - Headcount by Employment Type (Stacked 100%)
      getHeadcountByEmploymentTypeChart(timeSelection),
    ];
  },
};

// ============================================================================
// Page 2: Overwork Alert - Wholesale (労働時間アラート - 法人)
// ============================================================================

const page2Header = `- **目的**：残業時間が平均よりも高い・昨年同月比よりも高い拠点の早期検出と対処
- **今月のハイライト**：A・C拠点が、全体平均や昨年同期比よりも残業時間が長く、要介入拠点`;

const page2Footer = `脚注1：ツーリズム（法人）および（個人）の、西日本の各事業部・支店について初期的に分析`;

const page2Config: PageConfig = {
  pageId: '2',
  dashboardId: DASHBOARD_ID,
  pageNumber: 2,
  enabled: true,
  title: {
    [Languages.EN]: 'Overwork alert_Wholesale',
    [Languages.JA]: '労働時間アラート（法人）',
  },
  header: page2Header,
  footer: page2Footer,
  pageOptions: {
    displayOptions: {
      enabled: false,
    },
    timeOptions: {
      timeSliderEnabled: true,
      timeSliderType: TimeSliderTypes.POINT,
    },
    filterTray: {
      enabled: false,
    },
    dataView: {
      enabled: false,
    },
    segmentationLevel1: {
      enabled: false,
    },
    segmentationLevel2: {
      enabled: false,
    },
    benchmark: {
      enabled: false,
    },
    forecast: {
      enabled: false,
    },
  },
  gridCols: 20,
  configType: 'componentConfigs',
  components: ({ storyboardDateUtils }) => {
    const timeSelection = getLatestMonthTimeSelection(
      storyboardDateUtils.nowEndOfMonth
    );
    return [
      // Row 1: Left Top - Average Work Hours (Corporate, All)
      getAvgWorkhourFactbox(timeSelection, 'corporate'),
      // Row 1: Right Top - Average Work Hours (Corporate, Managers)
      getAvgWorkhourFactbox(timeSelection, 'corporate', {
        additionalFilter: customField3SupervisorFilter,
        chartIdSuffix: '-managers',
      }),
      // Row 2: Left Bottom - By Branch (Corporate, All)
      getAvgWorkhourByBranchChart(timeSelection, 'corporate'),
      // Row 2: Right Bottom - By Branch (Corporate, Managers)
      getAvgWorkhourByBranchChart(timeSelection, 'corporate', {
        additionalFilter: customField3SupervisorFilter,
        chartIdSuffix: '-managers',
      }),
    ];
  },
};

// ============================================================================
// Page 3: Overwork Alert - Retail (労働時間アラート - 個人)
// ============================================================================

const page3Header = `- **目的**：残業時間が平均よりも高い・昨年同月比よりも高い拠点の早期検出と対処
- **今月のハイライト**：A・C拠点が、全体平均や昨年同期比よりも残業時間が長く、要介入拠点`;

const page3Footer = `脚注1：ツーリズム（法人）および（個人）の、西日本の各事業部・支店について初期的に分析`;

const page3Config: PageConfig = {
  pageId: '3',
  dashboardId: DASHBOARD_ID,
  pageNumber: 3,
  enabled: true,
  title: {
    [Languages.EN]: 'Overwork alert_Retail',
    [Languages.JA]: '労働時間アラート（個人）',
  },
  header: page3Header,
  footer: page3Footer,
  pageOptions: {
    displayOptions: {
      enabled: false,
    },
    timeOptions: {
      timeSliderEnabled: true,
      timeSliderType: TimeSliderTypes.POINT,
    },
    filterTray: {
      enabled: false,
    },
    dataView: {
      enabled: false,
    },
    segmentationLevel1: {
      enabled: false,
    },
    segmentationLevel2: {
      enabled: false,
    },
    benchmark: {
      enabled: false,
    },
    forecast: {
      enabled: false,
    },
  },
  gridCols: 20,
  configType: 'componentConfigs',
  components: ({ storyboardDateUtils }) => {
    const timeSelection = getLatestMonthTimeSelection(
      storyboardDateUtils.nowEndOfMonth
    );
    return [
      // Row 1: Left Top - Average Work Hours (Retail, All)
      getAvgWorkhourFactbox(timeSelection, 'retail'),
      // Row 1: Right Top - Average Work Hours (Retail, Managers)
      getAvgWorkhourFactbox(timeSelection, 'retail', {
        additionalFilter: customField3SupervisorFilter,
        chartIdSuffix: '-managers',
      }),
      // Row 2: Left Bottom - By Branch (Retail, All)
      getAvgWorkhourByBranchChart(timeSelection, 'retail'),
      // Row 2: Right Bottom - By Branch (Retail, Managers)
      getAvgWorkhourByBranchChart(timeSelection, 'retail', {
        additionalFilter: customField3SupervisorFilter,
        chartIdSuffix: '-managers',
      }),
    ];
  },
};

// ============================================================================
// Dashboard Config Export
// ============================================================================

export const JTBHumanCapitalManagementDashboardConfig: DashboardConfig = {
  id: DASHBOARD_ID,
  title: {
    [Languages.EN]: 'Human Capital Management',
    [Languages.JA]: '人的資本経営ダッシュボード',
  },
  enabled: true,
  qaOnly: true,
  pages: [page1Config, page2Config, page3Config],
};
